import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../contexts/DataContext';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { guestEmailAPI, emailTemplatesAPI } from '../../services/api';

const { FiEdit2, FiTrash2, FiCopy, FiMail, FiCalendar, FiUser, FiUsers, FiEye, FiFilter, FiSearch, FiCheckCircle, FiArchive, FiMoreVertical, FiHome, FiX, FiSend } = FiIcons;

const GuestPageList = ({ onEdit }) => {
  const { guestPages, deleteGuestPage, properties, updateGuestPage, dateFilter, isDateInFilter } = useData();
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedGuestPage, setSelectedGuestPage] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState({});
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const handleDelete = async (page) => {
    if (window.confirm(`Are you sure you want to delete the guest page for "${page.guestName}"? This action cannot be undone.`)) {
      try {
        await deleteGuestPage(page.id);
        toast.success('Guest page deleted successfully!');
      } catch (error) {
        toast.error('Failed to delete guest page.');
      }
    }
    setActiveDropdown(null);
  };

  const handleArchive = async (page) => {
    try {
      await updateGuestPage(page.id, { status: 'archived' });
      toast.success('Guest page archived successfully!');
    } catch (error) {
      toast.error('Failed to archive guest page.');
    }
    setActiveDropdown(null);
  };

  const handleUnarchive = async (page) => {
    try {
      await updateGuestPage(page.id, { status: 'active' });
      toast.success('Guest page unarchived successfully!');
    } catch (error) {
      toast.error('Failed to unarchive guest page.');
    }
    setActiveDropdown(null);
  };

  const getPreviewUrl = (slug) => {
    const baseUrl = window.location.href.split('#')[0];
    return `${baseUrl}#/portal/${slug}`;
  };

  const copyPortalLink = (page) => {
    navigator.clipboard.writeText(getPreviewUrl(page.slug));
    toast.success('Portal link copied to clipboard!');
  };

  // Load email templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templates = await emailTemplatesAPI.getAll();
        setEmailTemplates(templates);
      } catch (error) {
        console.error('Error loading email templates:', error);
      }
    };
    loadTemplates();
  }, []);

  const openEmailModal = (page) => {
    if (!page.guestEmail) {
      toast.error('No email address provided for this guest. Please add an email address in the guest page settings.');
      return;
    }
    setSelectedGuestPage(page);
    setShowEmailModal(true);
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setSelectedGuestPage(null);
  };

  const sendTemplateEmail = async (templateType) => {
    if (!selectedGuestPage) return;

    setIsSendingEmail(true);
    try {
      const result = await guestEmailAPI.send(selectedGuestPage.id, templateType);
      if (result.success) {
        toast.success(`Email sent successfully to ${selectedGuestPage.guestEmail}!`);
        closeEmailModal();
      } else {
        toast.error(result.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.message || 'Failed to send email. Please check your SMTP configuration.');
    } finally {
      setIsSendingEmail(false);
    }
  };


  const getPropertyName = (propertyId) => properties.find(p => p.id === propertyId)?.name || 'Unknown Property';

  const getFilteredPages = () => {
    return guestPages
      .filter(page => filter === 'all' || page.status === filter)
      .filter(page => selectedProperty === 'all' || page.propertyId === selectedProperty)
      .filter(page => isDateInFilter(page.checkInDate, dateFilter))
      .filter(page => 
        page.guestName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        page.guestEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        page.reservationId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
  };

  const filteredAndSortedPages = getFilteredPages()
    .filter(page => filter === 'all' || page.status === filter)
    .filter(page => selectedProperty === 'all' || page.propertyId === selectedProperty)
    .filter(page => isDateInFilter(page.checkInDate, dateFilter)) // Apply the global date filter
    .filter(page => 
      page.guestName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      page.guestEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      page.reservationId?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'checkInDate') return new Date(b.checkInDate) - new Date(a.checkInDate);
      if (sortBy === 'guestName') return a.guestName.localeCompare(b.guestName);
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

  const getStatusColor = (status) => ({
    active: 'bg-green-100 text-green-800 border-green-200',
    archived: 'bg-gray-100 text-gray-800 border-gray-200',
  }[status] || 'bg-blue-100 text-blue-800 border-blue-200');

  const getStatusIcon = (status) => ({
    active: FiCheckCircle,
    archived: FiArchive,
  }[status] || FiUser);

  const ActionDropdown = ({ page }) => (
    <div className="relative">
      <motion.button 
        onClick={() => setActiveDropdown(activeDropdown === page.id ? null : page.id)}
        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
      >
        <SafeIcon icon={FiMoreVertical} className="text-sm" />
      </motion.button>
      
      <AnimatePresence>
        {activeDropdown === page.id && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
          >
            {page.status === 'active' ? (
              <button 
                onClick={() => handleArchive(page)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
              >
                <SafeIcon icon={FiArchive} className="text-sm" />
                <span>Archive</span>
              </button>
            ) : (
              <button 
                onClick={() => handleUnarchive(page)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
              >
                <SafeIcon icon={FiCheckCircle} className="text-sm" />
                <span>Unarchive</span>
              </button>
            )}
            <div className="border-t border-gray-100 my-1"></div>
            {page.guestEmail && (
              <button 
                onClick={() => {
                  openEmailModal(page);
                  setActiveDropdown(null);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
              >
                <SafeIcon icon={FiMail} className="text-sm" />
                <span>Send Email</span>
              </button>
            )}
            <button 
              onClick={() => onEdit(page)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
            >
              <SafeIcon icon={FiEdit2} className="text-sm" />
              <span>Edit</span>
            </button>
            <button 
              onClick={() => handleDelete(page)}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
            >
              <SafeIcon icon={FiTrash2} className="text-sm" />
              <span>Delete</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  if (guestPages.length === 0) return (
    <div className="text-center py-12">
      <SafeIcon icon={FiUsers} className="text-gray-400 text-6xl mx-auto mb-4" />
      <h3 className="text-xl font-medium text-gray-900 mb-2">No Guest Pages Yet</h3>
      <p className="text-gray-600">Create your first guest portal page to get started.</p>
    </div>
  );

  return (
    <div>
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm p-4 mb-6 border border-gray-200"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SafeIcon icon={FiSearch} className="text-gray-400" />
            </div>
            <input 
              type="text" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search guests, emails, or reservations..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Property Filter */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiHome} className="text-gray-400 text-xs" />
              </div>
              <select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                className="pl-8 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm max-w-[180px] truncate"
              >
                <option value="all">All Properties</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="createdAt">Sort by Created</option>
              <option value="checkInDate">Sort by Check-in</option>
              <option value="guestName">Sort by Name</option>
            </select>
            
            <div className="flex items-center bg-gray-100 rounded-lg p-1 hidden sm:flex">
              <button 
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1 rounded-md text-sm ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
              >
                Grid
              </button>
              <button 
                onClick={() => setViewMode('list')}
                className={`px-3 py-1 rounded-md text-sm ${viewMode === 'list' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
              >
                List
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-4">
            <SafeIcon icon={FiFilter} className="text-gray-400" />
            <div className="flex space-x-2">
              {[
                { key: 'all', label: 'All Pages', count: guestPages.length },
                { key: 'active', label: 'Active', count: guestPages.filter(p => p.status === 'active').length },
                { key: 'archived', label: 'Archived', count: guestPages.filter(p => p.status === 'archived').length }
              ].map((tab) => (
                <motion.button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center space-x-2 text-sm ${filter === tab.key ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                >
                  <span>{tab.label}</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${filter === tab.key ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-700'}`}>
                    {tab.count}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>
          
          {dateFilter !== 'All Time' && (
            <div className="flex items-center space-x-2 bg-blue-50 px-3 py-1.5 rounded-full text-xs font-medium text-blue-700 border border-blue-100">
              <SafeIcon icon={FiCalendar} className="text-blue-500" />
              <span>Filtering by: {dateFilter} (Arrivals)</span>
            </div>
          )}
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        <motion.div 
          key={viewMode}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : 'space-y-4'}
        >
          {filteredAndSortedPages.map((page, index) => (
            <motion.div
              key={page.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-all duration-200"
            >
              {viewMode === 'grid' ? (
                <div>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{page.guestName}</h3>
                      {/* Enriched Property Display */}
                      <div className="flex items-center text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded-md w-fit mt-1">
                        <SafeIcon icon={FiHome} className="mr-1.5 text-xs" />
                        <span className="font-medium">{getPropertyName(page.propertyId)}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(page.status)}`}>
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={getStatusIcon(page.status)} className="text-xs" />
                          <span>{page.status}</span>
                        </div>
                      </span>
                      <ActionDropdown page={page} />
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <div className="flex items-center">
                      <SafeIcon icon={FiCalendar} className="mr-2" />
                      <span>{format(new Date(page.checkInDate), 'MMM d')} - {format(new Date(page.checkOutDate), 'MMM d, yyyy')}</span>
                    </div>
                    {page.reservationId && (
                      <div className="flex items-center">
                        <SafeIcon icon={FiUser} className="mr-2" />
                        <span>Reservation: {page.reservationId}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button 
                      onClick={() => window.open(getPreviewUrl(page.slug), '_blank')}
                      className="flex items-center space-x-2 bg-purple-50 hover:bg-purple-100 text-purple-700 px-3 py-2 rounded-lg text-sm"
                    >
                      <SafeIcon icon={FiEye} className="text-xs" />
                      <span>Preview</span>
                    </button>
                    <button 
                      onClick={() => copyPortalLink(page)}
                      className="flex items-center space-x-2 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded-lg text-sm"
                    >
                      <SafeIcon icon={FiCopy} className="text-xs" />
                      <span>Copy Link</span>
                    </button>
                    <button 
                      onClick={() => openEmailModal(page)}
                      disabled={!page.guestEmail}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm ${
                        page.guestEmail 
                          ? 'bg-green-50 hover:bg-green-100 text-green-700' 
                          : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                      }`}
                      title={!page.guestEmail ? 'No email address for this guest' : 'Send email to guest'}
                    >
                      <SafeIcon icon={FiMail} className="text-xs" />
                      <span>Send Email</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-lg ${getStatusColor(page.status)}`}>
                      <SafeIcon icon={getStatusIcon(page.status)} className="text-lg" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{page.guestName}</h3>
                      <div className="flex items-center mt-1">
                         <span className="flex items-center text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                           <SafeIcon icon={FiHome} className="mr-1 text-[10px]" />
                           {getPropertyName(page.propertyId)}
                         </span>
                      </div>
                      <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                        <span className="flex items-center">
                          <SafeIcon icon={FiCalendar} className="mr-1" />
                          {format(new Date(page.checkInDate), 'MMM d')} - {format(new Date(page.checkOutDate), 'MMM d')}
                        </span>
                        {page.reservationId && <span className="flex items-center"><SafeIcon icon={FiUser} className="mr-1" />{page.reservationId}</span>}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <button onClick={() => window.open(getPreviewUrl(page.slug), '_blank')} className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg" title="Preview"><SafeIcon icon={FiEye} /></button>
                    <button onClick={() => copyPortalLink(page)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Copy Link"><SafeIcon icon={FiCopy} /></button>
                    {page.guestEmail && (
                      <button onClick={() => openEmailModal(page)} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg" title="Send Email"><SafeIcon icon={FiMail} /></button>
                    )}
                    {page.status === 'active' ? (
                      <button onClick={() => handleArchive(page)} className="p-2 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg" title="Archive"><SafeIcon icon={FiArchive} /></button>
                    ) : (
                      <button onClick={() => handleUnarchive(page)} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg" title="Unarchive"><SafeIcon icon={FiCheckCircle} /></button>
                    )}
                    <button onClick={() => onEdit(page)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit"><SafeIcon icon={FiEdit2} /></button>
                    <button onClick={() => handleDelete(page)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete"><SafeIcon icon={FiTrash2} /></button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>
      
      {filteredAndSortedPages.length === 0 && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <SafeIcon icon={FiSearch} className="text-gray-400 text-6xl mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">No Results Found</h3>
          <p className="text-gray-600">
            {dateFilter !== 'All Time' 
              ? `No arrivals found for ${dateFilter} matching your criteria.` 
              : 'Try adjusting your search or filter criteria.'}
          </p>
        </motion.div>
      )}

      {activeDropdown && (
        <div className="fixed inset-0 z-0" onClick={() => setActiveDropdown(null)} />
      )}

      {/* Email Template Selection Modal */}
      <AnimatePresence>
        {showEmailModal && selectedGuestPage && (
          <>
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={closeEmailModal}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Send Email</h3>
                  <button
                    onClick={closeEmailModal}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <SafeIcon icon={FiX} />
                  </button>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Send an email to <span className="font-semibold text-gray-900">{selectedGuestPage.guestName}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {selectedGuestPage.guestEmail}
                  </p>
                </div>

                <div className="space-y-2 mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Email Template
                  </label>
                  {[
                    { key: 'welcome', label: 'Welcome Email', description: 'Initial welcome message with portal link' },
                    { key: 'checkin_reminder', label: 'Check-in Reminder', description: 'Reminder before check-in date' },
                    { key: 'checkout_reminder', label: 'Check-out Reminder', description: 'Reminder before check-out date' },
                  ].map((template) => {
                    const templateData = emailTemplates[template.key];
                    const hasContent = templateData?.subject && templateData?.body;
                    
                    return (
                      <button
                        key={template.key}
                        onClick={() => sendTemplateEmail(template.key)}
                        disabled={isSendingEmail || !hasContent}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          hasContent
                            ? 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                            : 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">{template.label}</h4>
                            <p className="text-xs text-gray-500">{template.description}</p>
                            {!hasContent && (
                              <p className="text-xs text-yellow-600 mt-1">⚠️ Template not configured</p>
                            )}
                          </div>
                          {hasContent && (
                            <SafeIcon icon={FiSend} className="text-blue-600 ml-2" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {isSendingEmail && (
                  <div className="text-center py-2">
                    <p className="text-sm text-gray-600">Sending email...</p>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </div>
  );
};

export default GuestPageList;